import React, { memo, useId, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { YEAR_MAX, YEAR_MIN, YEAR_OVERSCAN, YEAR_ROW_REM, YEAR_VISIBLE_ROWS } from './constants';
import styles from './PeriodPicker.module.scss';

const CENTER_OFFSET = Math.floor(YEAR_VISIBLE_ROWS / 2);

const KEY_STEPS: Record<string, number> = {
  ArrowUp: -1,
  ArrowDown: 1,
  PageUp: -YEAR_VISIBLE_ROWS,
  PageDown: YEAR_VISIBLE_ROWS,
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getRowPx = () => YEAR_ROW_REM * Number.parseFloat(getComputedStyle(document.documentElement).fontSize);

interface YearListProps {
  value: number;
  onSelect: (year: number) => void;
  min?: number;
  max?: number;
}

// Windowed: only the rows in view (plus overscan) are rendered, so the full year span stays cheap.
const YearList = ({ value, onSelect, min = YEAR_MIN, max = YEAR_MAX }: YearListProps) => {
  const optionIdPrefix = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const rowPxRef = useRef(0);
  const [activeYear, setActiveYear] = useState(value);
  const [firstIndex, setFirstIndex] = useState(Math.max(0, value - min - CENTER_OFFSET));

  const count = max - min + 1;
  const start = Math.max(0, firstIndex - YEAR_OVERSCAN);
  const end = Math.min(count, firstIndex + YEAR_VISIBLE_ROWS + YEAR_OVERSCAN);

  // Centres the selected year before paint. A popover mounts its content hidden, where
  // scrollTop writes are ignored, so centring waits until the list has a size.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    rowPxRef.current = getRowPx();

    const centre = () => {
      list.scrollTop = (value - min - CENTER_OFFSET) * rowPxRef.current;
      setFirstIndex(Math.floor(list.scrollTop / rowPxRef.current));
    };

    if (list.clientHeight) {
      centre();
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      if (!list.clientHeight) return;
      centre();
      observer.disconnect();
    });
    observer.observe(list);
    return () => observer.disconnect();
  }, [value, min]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    setFirstIndex(Math.floor(event.currentTarget.scrollTop / rowPxRef.current));
  };

  const handleOptionClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onSelect(Number(event.currentTarget.dataset.year));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(activeYear);
      return;
    }
    const step = KEY_STEPS[event.key];
    if (!step) return;
    event.preventDefault();

    const nextYear = clamp(activeYear + step, min, max);
    setActiveYear(nextYear);

    const list = event.currentTarget;
    const rowPx = rowPxRef.current;
    const top = (nextYear - min) * rowPx;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (top + rowPx > list.scrollTop + list.clientHeight) list.scrollTop = top + rowPx - list.clientHeight;
  };

  const rows: React.ReactNode[] = [];
  for (let index = start; index < end; index += 1) {
    const year = min + index;
    rows.push(
      <div
        key={year}
        id={`${optionIdPrefix}-${year}`}
        role="option"
        aria-selected={year === value}
        data-year={year}
        className={clsx(styles.yearListOption, {
          [styles.yearListOptionSelected]: year === value,
          [styles.yearListOptionActive]: year === activeYear,
        })}
        style={{ '--year-index': index } as React.CSSProperties}
        onClick={handleOptionClick}
      >
        {year}
      </div>,
    );
  }

  return (
    <div className={styles.periodPicker}>
      <p className={styles.periodPickerTitle}>Pick a year</p>
      <div
        ref={listRef}
        role="listbox"
        tabIndex={0}
        aria-label="Years"
        aria-activedescendant={`${optionIdPrefix}-${activeYear}`}
        className={styles.yearList}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.yearListTrack} style={{ '--year-count': count } as React.CSSProperties}>
          {rows}
        </div>
      </div>
    </div>
  );
};

export default memo(YearList);
